// -- [Q&A / FAQ - Radix Accordion; answers support markdown (links, bold)]

import * as Accordion from '@radix-ui/react-accordion'
import ReactMarkdown from 'react-markdown'
import type { FaqItem } from '@/types/content'
import { Section } from '@/components/ui/Section'

interface FaqSectionProps {
  faq: FaqItem[]
}

// -- External links open in new tab with safe rel
function FaqLink({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  const isExternal = href?.startsWith('http')
  return (
    <a
      href={href}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      className="text-brand-orange underline hover:no-underline"
      {...props}
    >
      {children}
    </a>
  )
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  )
}

export function FaqSection({ faq }: FaqSectionProps) {
  return (
    <Section id="qa" bg="white" narrow>
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 motion-enter">Q&A</h2>
      <p className="text-ink-muted mb-10">Common questions about Drivible.</p>
      <Accordion.Root type="multiple" defaultValue={[]} className="space-y-2">
        {faq.map((item, i) => (
          <Accordion.Item
            key={i}
            value={`item-${i}`}
            className="rounded-lg border border-line overflow-hidden data-[state=open]:border-brand-orange"
          >
            <Accordion.Header>
              <Accordion.Trigger
                className="group flex w-full items-center justify-between gap-3 px-4 py-3 text-left font-medium text-gray-900 hover:bg-surface-muted transition-colors data-[state=open]:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-orange"
                data-umami-event="Q&A open"
                data-umami-event-index={String(i)}
              >
                {item.question}
                <ChevronIcon className="ml-2 shrink-0 text-brand-orange transition-transform duration-200 group-data-[state=open]:rotate-180" />
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className="px-4 py-3 text-ink-muted border-t border-line [&_p]:my-1 [&_strong]:font-semibold [&_strong]:text-gray-800">
              <ReactMarkdown components={{ a: FaqLink }}>{item.answer}</ReactMarkdown>
            </Accordion.Content>
          </Accordion.Item>
        ))}
      </Accordion.Root>
    </Section>
  )
}
